import React, { useEffect, useState } from 'react';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { getScheduleSummary } from '../../core/utils/scheduleEditorUtils';
import WorkoutDurationSummary from '../shell/WorkoutDurationSummary';

export default function WorkoutScheduleSummary({
  planName = '',
  planDescription = '',
  startDateLabel,
  endDateLabel,
  startDate = '',
  endDate = '',
  onSaveDates,
  savingDates = false,
  onSavePlanDetails,
  savingPlanDetails = false,
  onJumpToDay,
  selectedWeekdayId,
  assigningWeekdayId = null,
  weekdayDetails,
  durationSummary = null,
}) {
  const { totalDays, assignedDays } = getScheduleSummary(weekdayDetails);
  const days = Object.entries(weekdayDetails || {});
  const [editingDates, setEditingDates] = useState(false);
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd, setDraftEnd] = useState(endDate);
  const [editingDetails, setEditingDetails] = useState(false);
  const [draftName, setDraftName] = useState(planName);
  const [draftDescription, setDraftDescription] = useState(planDescription);

  useEffect(() => {
    if (!editingDates) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
    }
  }, [startDate, endDate, editingDates]);

  useEffect(() => {
    if (!editingDetails) {
      setDraftName(planName);
      setDraftDescription(planDescription);
    }
  }, [planName, planDescription, editingDetails]);

  const handleSaveDates = async () => {
    if (!onSaveDates) return;
    await onSaveDates(draftStart, draftEnd);
    setEditingDates(false);
  };

  const handleSaveDetails = async () => {
    if (!onSavePlanDetails) return;
    const ok = await onSavePlanDetails(draftName.trim(), draftDescription.trim());
    if (ok !== false) setEditingDetails(false);
  };

  const dateRangeLabel = `${startDateLabel} – ${endDateLabel}`;
  const detailsBusy = savingDates || savingPlanDetails;

  return (
    <div className="mb-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          {planName ? (
            <p className="text-base font-semibold text-gray-900 truncate" title={planName}>
              {planName}
            </p>
          ) : null}
          {planDescription ? (
            <p className="text-sm text-gray-600 mt-0.5 line-clamp-2" title={planDescription}>
              {planDescription}
            </p>
          ) : null}
          <p className={`text-sm ${planName ? 'text-gray-600' : 'font-semibold text-gray-900'}`}>
            {dateRangeLabel}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            {assignedDays}/{totalDays} days assigned
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <WorkoutDurationSummary summary={durationSummary} />
          {onSavePlanDetails && !editingDetails && !editingDates && (
            <button
              type="button"
              onClick={() => {
                setDraftName(planName);
                setDraftDescription(planDescription);
                setEditingDetails(true);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-orange-700 px-2.5 py-1.5 rounded-lg hover:bg-orange-50 border border-gray-200 shrink-0"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit name
            </button>
          )}
          {onSaveDates && !editingDates && !editingDetails && (
            <button
              type="button"
              onClick={() => setEditingDates(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-orange-700 px-2.5 py-1.5 rounded-lg hover:bg-orange-50 border border-gray-200 shrink-0"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit dates
            </button>
          )}
        </div>
      </div>

      {editingDetails && onSavePlanDetails && (
        <div className="mb-3 flex flex-col gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Plan name</label>
            <input
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              disabled={savingPlanDetails}
              placeholder="e.g. Upper body strength"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              rows={2}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white resize-none"
              disabled={savingPlanDetails}
              placeholder="Brief description of this workout plan"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleSaveDetails}
              disabled={savingPlanDetails || !draftName.trim()}
              className="text-xs font-medium bg-orange-primary text-white px-3 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50"
            >
              {savingPlanDetails ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setDraftName(planName);
                setDraftDescription(planDescription);
                setEditingDetails(false);
              }}
              disabled={savingPlanDetails}
              className="text-xs font-medium text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {editingDates && onSaveDates && (
        <div className="mb-3 flex flex-wrap items-end gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
            <input
              type="date"
              value={draftStart}
              onChange={(e) => setDraftStart(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              disabled={detailsBusy}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
            <input
              type="date"
              value={draftEnd}
              onChange={(e) => setDraftEnd(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              disabled={detailsBusy}
            />
          </div>
          <button
            type="button"
            onClick={handleSaveDates}
            disabled={detailsBusy}
            className="text-xs font-medium bg-orange-primary text-white px-3 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50"
          >
            {savingDates ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => {
              setDraftStart(startDate);
              setDraftEnd(endDate);
              setEditingDates(false);
            }}
            disabled={detailsBusy}
            className="text-xs font-medium text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}

      {days.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {days.map(([key, day]) => {
            const hasPlan = (day.plans || []).length > 0;
            const planTitle = (day.plans?.[0]?.title || '').trim();
            const weekdayId = day.weekday_id ?? key;
            const isSelected =
              selectedWeekdayId != null &&
              String(selectedWeekdayId) === String(weekdayId);
            const isAssigning =
              assigningWeekdayId != null &&
              String(assigningWeekdayId) === String(weekdayId);

            const statusClasses = isSelected
              ? 'bg-orange-primary text-white border-orange-primary font-semibold shadow-sm'
              : 'bg-transparent text-gray-800 border-gray-300 hover:border-gray-400 hover:bg-gray-50/80';

            return (
              <button
                key={key}
                type="button"
                onClick={() => onJumpToDay?.(weekdayId, day)}
                disabled={isAssigning}
                title={planTitle || undefined}
                className={`inline-flex flex-col items-start gap-0.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-70 border min-w-[4.5rem] max-w-[9rem] ${statusClasses}`}
              >
                <span className="inline-flex items-center gap-1.5 capitalize">
                  {isAssigning ? (
                    <Loader2
                      className={`w-4 h-4 animate-spin shrink-0 ${isSelected ? 'text-white' : 'text-orange-500'}`}
                    />
                  ) : hasPlan ? (
                    <Check
                      className={`w-4 h-4 shrink-0 ${isSelected ? 'text-white' : 'text-green-600'}`}
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  ) : (
                    <X
                      className={`w-4 h-4 shrink-0 ${isSelected ? 'text-white' : 'text-red-500'}`}
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  )}
                  {day.name}
                </span>
                {planTitle ? (
                  <span
                    className={`w-full truncate text-[10px] font-normal leading-tight ${
                      isSelected ? 'text-orange-100' : 'text-gray-500'
                    }`}
                  >
                    {planTitle}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
